/* S. Studio — watch-3d.js
   Procedural 3D wristwatch built with Three.js. No model files required.
   Mounts on any element with [data-watch-3d]; hands show the real time.
   Options via data attributes: data-metal, data-dial, data-strap, data-auto-rotate, data-interactive,
   data-style="diver" (rotating ceramic bezel, lume plots, Mercedes hands, date + cyclops), data-bezel,
   data-brand / data-line1 / data-model / data-line2 (dial text). */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const PRESETS = {
  metal: { steel: 0xd9d9dc, gold: 0xd9b46b, rose: 0xe2a88e, black: 0x2b2b2f },
  dial: { black: 0x0b0b0e, navy: 0x0d1a36, green: 0x0d2a22, white: 0xe9e4da, champagne: 0xcdb58a, burgundy: 0x4b0f1e },
  strap: { bracelet: null, leather: 0x1e1512, rubber: 0x121212, blue: 0x10203c }
};

function dialTexture(hex, light) {
  const size = 1024;
  const c = document.createElement('canvas'); c.width = c.height = size;
  const ctx = c.getContext('2d');
  const base = '#' + hex.toString(16).padStart(6, '0');
  // Sunburst: radial gradient + fine radial lines
  const g = ctx.createRadialGradient(size / 2, size / 2, 20, size / 2, size / 2, size / 2);
  g.addColorStop(0, light ? '#ffffff' : '#ffffff');
  g.addColorStop(0.02, base);
  g.addColorStop(1, light ? '#b8b2a6' : '#000000');
  ctx.fillStyle = base; ctx.fillRect(0, 0, size, size);
  ctx.globalAlpha = light ? 0.35 : 0.55; ctx.fillStyle = g; ctx.fillRect(0, 0, size, size); ctx.globalAlpha = 1;
  ctx.save(); ctx.translate(size / 2, size / 2);
  ctx.strokeStyle = light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.045)'; ctx.lineWidth = 1;
  for (let i = 0; i < 360; i += 1.5) {
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(i * Math.PI / 180) * size, Math.sin(i * Math.PI / 180) * size); ctx.stroke();
  }
  // Minute track
  const ink = light ? 'rgba(20,20,20,0.85)' : 'rgba(240,235,225,0.85)';
  ctx.strokeStyle = ink;
  for (let i = 0; i < 60; i++) {
    const a = i * Math.PI / 30; const r1 = size * 0.455, r2 = i % 5 === 0 ? size * 0.43 : size * 0.44;
    ctx.lineWidth = i % 5 === 0 ? 3 : 1.5;
    ctx.beginPath(); ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1); ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2); ctx.stroke();
  }
  ctx.fillStyle = ink; ctx.textAlign = 'center';
  ctx.font = '600 44px "Cormorant Garamond", Georgia, serif'; ctx.fillText('S. STUDIO', 0, -size * 0.19);
  ctx.font = '300 22px Manrope, sans-serif'; ctx.letterSpacing = '6px';
  ctx.fillText('AUTOMATIC', 0, size * 0.25);
  ctx.font = '300 16px Manrope, sans-serif'; ctx.fillText('SWISS MADE', 0, size * 0.42);
  ctx.restore();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 8;
  return tex;
}

/* ---------- Diver style (Submariner-type): glossy dial, ceramic bezel, lume plots, Mercedes hands ---------- */
const BEZELS = { black: '#0c0c0e', green: '#0b3d24', blue: '#0c2350' };

function diverDialTexture(hex, opts) {
  const size = 1024, half = size / 2;
  const c = document.createElement('canvas'); c.width = c.height = size;
  const ctx = c.getContext('2d');
  const base = '#' + hex.toString(16).padStart(6, '0');
  ctx.fillStyle = base; ctx.fillRect(0, 0, size, size);
  // Glossy lacquer: soft off-centre highlight, no sunburst
  const g = ctx.createRadialGradient(half * 0.7, half * 0.6, 10, half, half, half);
  g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(0.5, 'rgba(255,255,255,0.02)'); g.addColorStop(1, 'rgba(0,0,0,0.35)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  ctx.save(); ctx.translate(half, half);
  const ink = 'rgba(238,238,240,0.92)';
  // Minute track
  ctx.strokeStyle = ink;
  for (let i = 0; i < 60; i++) {
    const a = i * Math.PI / 30, r1 = half * 0.985, r2 = half * (i % 5 === 0 ? 0.94 : 0.955);
    ctx.lineWidth = i % 5 === 0 ? 3 : 1.6;
    ctx.beginPath(); ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1); ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2); ctx.stroke();
  }
  // Date window at 3 o'clock
  const dx = half * (0.62 / 0.86), dw = 64, dh = 50;
  ctx.fillStyle = '#f4f2ec'; ctx.fillRect(dx - dw / 2, -dh / 2, dw, dh);
  ctx.fillStyle = '#111'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.font = '700 36px "Helvetica Neue", Arial, sans-serif'; ctx.fillText(String(new Date().getDate()), dx, 2);
  // Text
  ctx.fillStyle = ink; ctx.textBaseline = 'alphabetic';
  ctx.font = '600 50px "Cormorant Garamond", Georgia, serif'; ctx.fillText(opts.brand || 'S. STUDIO', 0, -half * 0.36);
  ctx.font = '500 17px "Helvetica Neue", Arial, sans-serif';
  if (opts.line1) ctx.fillText(opts.line1, 0, -half * 0.28);
  if (opts.model) { ctx.font = '600 22px "Helvetica Neue", Arial, sans-serif'; ctx.fillText(opts.model, 0, half * 0.33); }
  ctx.font = '500 15px "Helvetica Neue", Arial, sans-serif';
  if (opts.line2) ctx.fillText(opts.line2, 0, half * 0.395);
  ctx.font = '500 12px "Helvetica Neue", Arial, sans-serif'; ctx.fillText('SWISS MADE', 0, half * 0.9);
  ctx.restore();
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 8;
  return tex;
}

function bezelTexture(color) {
  // RingGeometry UVs span the outer radius (1.02), so canvas radius 512 = 1.02 units.
  const size = 1024, half = size / 2, outer = 1.02, rIn = half * (0.87 / outer);
  const c = document.createElement('canvas'); c.width = c.height = size;
  const ctx = c.getContext('2d');
  ctx.translate(half, half);
  ctx.fillStyle = color; ctx.beginPath(); ctx.arc(0, 0, half, 0, Math.PI * 2); ctx.arc(0, 0, rIn, 0, Math.PI * 2, true); ctx.fill();
  const g = ctx.createLinearGradient(-half, -half, half, half);
  g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(0.5, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(255,255,255,0.06)');
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, half, 0, Math.PI * 2); ctx.arc(0, 0, rIn, 0, Math.PI * 2, true); ctx.fill();
  const print = '#d9d9db', mid = (half + rIn) / 2;
  ctx.fillStyle = print; ctx.strokeStyle = print; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let m = 0; m < 60; m++) {
    const a = (m / 60) * Math.PI * 2;
    ctx.save(); ctx.rotate(a);
    if (m === 0) {
      // Triangle with lume pip at 12
      ctx.beginPath(); ctx.moveTo(0, -rIn - 6); ctx.lineTo(-30, -half + 12); ctx.lineTo(30, -half + 12); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#f3f1e7'; ctx.beginPath(); ctx.arc(0, -half + 30, 9, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = print;
    } else if (m % 10 === 0) {
      ctx.font = '600 44px "Helvetica Neue", Arial, sans-serif'; ctx.fillText(String(m), 0, -mid);
    } else if (m % 5 === 0) {
      ctx.fillRect(-5, -half + 14, 10, 34);
    } else if (m < 15) {
      ctx.fillRect(-2, -half + 14, 4, 22);
    }
    ctx.restore();
  }
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 8;
  return tex;
}

function flatShape(points, depth) {
  // Shape drawn pointing +y, laid flat so it points toward 12 o'clock (-z) and extrudes upward (+y).
  const s = new THREE.Shape(); points.forEach(([x, y], i) => (i ? s.lineTo(x, y) : s.moveTo(x, y)));
  const geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false });
  geo.rotateX(-Math.PI / 2);
  return geo;
}

function buildDiver(watch, opts, mats) {
  const { polished, brushed, glassMat } = mats;
  const whiteGold = new THREE.MeshStandardMaterial({ color: 0xeceef1, metalness: 1, roughness: 0.16, side: THREE.DoubleSide });
  const lume = new THREE.MeshStandardMaterial({ color: 0xf3f1e7, emissive: 0x6f8a6a, emissiveIntensity: 0.22, roughness: 0.45, side: THREE.DoubleSide });

  // Rotating bezel: brushed carrier with coin-edge knurling + glossy ceramic insert
  const carrier = new THREE.Mesh(new THREE.LatheGeometry([[0.86, 0.13], [1.045, 0.13], [1.07, 0.17], [1.055, 0.235], [0.86, 0.235]].map((p) => new THREE.Vector2(p[0], p[1])), 128), brushed);
  carrier.material.side = THREE.DoubleSide; watch.add(carrier);
  const knurl = new THREE.BoxGeometry(0.014, 0.075, 0.03);
  for (let i = 0; i < 120; i++) {
    const a = (i / 120) * Math.PI * 2, k = new THREE.Mesh(knurl, polished);
    k.position.set(Math.sin(a) * 1.066, 0.185, Math.cos(a) * 1.066); k.rotation.y = a; watch.add(k);
  }
  const insertMat = new THREE.MeshPhysicalMaterial({ map: bezelTexture(BEZELS[opts.bezel] || BEZELS.black), metalness: 0.05, roughness: 0.3, clearcoat: 0.6, clearcoatRoughness: 0.18, envMapIntensity: 0.55, transparent: true });
  const insert = new THREE.Mesh(new THREE.RingGeometry(0.87, 1.02, 128, 1), insertMat);
  insert.rotation.x = -Math.PI / 2; insert.position.y = 0.238; watch.add(insert);

  // Lume plots with white-gold surrounds; triangle at 12, bars at 6 and 9, date at 3
  const r = 0.66;
  const plot = new THREE.CylinderGeometry(0.055, 0.055, 0.03, 32), ring = new THREE.TorusGeometry(0.058, 0.009, 10, 40);
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2, x = Math.sin(a) * r, z = -Math.cos(a) * r;
    if (i === 3) continue;
    if (i === 0) {
      const tri = new THREE.Mesh(flatShape([[-0.085, 0.06], [0.085, 0.06], [0, -0.09]], 0.03), lume);
      tri.position.set(0, 0.075, -0.66); watch.add(tri);
      const triFrame = new THREE.Mesh(flatShape([[-0.1, 0.072], [0.1, 0.072], [0, -0.108]], 0.02), whiteGold);
      triFrame.position.set(0, 0.07, -0.66); watch.add(triFrame);
    } else if (i === 6 || i === 9) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.03, 0.2), lume); bar.position.set(x, 0.09, z); bar.rotation.y = -a; watch.add(bar);
      const fr = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.022, 0.22), whiteGold); fr.position.set(x, 0.083, z); fr.rotation.y = -a; watch.add(fr);
    } else {
      const p = new THREE.Mesh(plot, lume); p.position.set(x, 0.09, z); watch.add(p);
      const s = new THREE.Mesh(ring, whiteGold); s.rotation.x = Math.PI / 2; s.position.set(x, 0.1, z); watch.add(s);
    }
  }

  // Hands
  const hands = {};
  const pivotAt = (y) => { const p = new THREE.Group(); p.position.y = y; watch.add(p); return p; };
  // Hour: Mercedes hand
  hands.hour = pivotAt(0.11);
  const hBar = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.016, 0.26), whiteGold); hBar.position.z = -0.11; hands.hour.add(hBar);
  const merc = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.012, 10, 40), whiteGold); merc.rotation.x = Math.PI / 2; merc.position.z = -0.31; hands.hour.add(merc);
  const mercLume = new THREE.Mesh(new THREE.CylinderGeometry(0.066, 0.066, 0.012, 32), lume); mercLume.position.set(0, -0.002, -0.31); hands.hour.add(mercLume);
  [0, 2.094, 4.189].forEach((ang) => { const sp = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.018, 0.075), whiteGold); sp.position.set(Math.sin(ang) * 0.037, 0.001, -0.31 - Math.cos(ang) * 0.037); sp.rotation.y = -ang; hands.hour.add(sp); });
  const hTip = new THREE.Mesh(flatShape([[-0.03, 0], [0.03, 0], [0, 0.07]], 0.016), whiteGold); hTip.position.set(0, -0.008, -0.38); hands.hour.add(hTip);
  // Minute: sword hand with lume fill
  hands.minute = pivotAt(0.125);
  hands.minute.add(new THREE.Mesh(flatShape([[-0.014, -0.06], [-0.045, 0.14], [0, 0.7], [0.045, 0.14], [0.014, -0.06]], 0.016), whiteGold));
  const mLume = new THREE.Mesh(flatShape([[-0.026, 0.16], [0, 0.6], [0.026, 0.16]], 0.006), lume); mLume.position.y = 0.012; hands.minute.add(mLume);
  // Seconds: lollipop
  hands.second = pivotAt(0.145);
  const sBar = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.012, 0.92), whiteGold); sBar.position.z = -0.28; hands.second.add(sBar);
  const lolly = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.042, 0.016, 32), lume); lolly.position.z = -0.52; hands.second.add(lolly);
  const lollyRing = new THREE.Mesh(new THREE.TorusGeometry(0.044, 0.007, 10, 32), whiteGold); lollyRing.rotation.x = Math.PI / 2; lollyRing.position.z = -0.52; hands.second.add(lollyRing);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.03, 32), whiteGold); cap.position.y = 0.16; watch.add(cap);

  // Crystal inside the bezel + cyclops lens over the date
  const diverGlass = glassMat.clone(); diverGlass.opacity = 0.1; diverGlass.envMapIntensity = 0.7;
  const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.87, 0.87, 0.03, 128), diverGlass); glass.position.y = 0.245; glass.renderOrder = 10; watch.add(glass);
  const cyclopsMat = glassMat.clone(); cyclopsMat.opacity = 0.28;
  const cyclops = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.05, 0.13), cyclopsMat); cyclops.position.set(0.62, 0.275, 0); cyclops.renderOrder = 11; watch.add(cyclops);
  return hands;
}

function buildWatch(opts) {
  const metalHex = PRESETS.metal[opts.metal] ?? PRESETS.metal.steel;
  const dialHex = PRESETS.dial[opts.dial] ?? PRESETS.dial.navy;
  const strapHex = opts.strap in PRESETS.strap ? PRESETS.strap[opts.strap] : null;
  const lightDial = opts.dial === 'white' || opts.dial === 'champagne';
  const accentHex = opts.metal === 'steel' || opts.metal === 'black' ? 0xd9b46b : metalHex;

  const polished = new THREE.MeshStandardMaterial({ color: metalHex, metalness: 1, roughness: 0.18 });
  const brushed = new THREE.MeshStandardMaterial({ color: metalHex, metalness: 1, roughness: 0.42 });
  const accent = new THREE.MeshStandardMaterial({ color: accentHex, metalness: 1, roughness: 0.22 });
  const diver = opts.style === 'diver';
  const dialMat = diver
    ? new THREE.MeshPhysicalMaterial({ map: diverDialTexture(dialHex, opts), metalness: 0, roughness: 0.42, clearcoat: 0.5, clearcoatRoughness: 0.22, envMapIntensity: 0.45 })
    : new THREE.MeshStandardMaterial({ map: dialTexture(dialHex, lightDial), metalness: 0.25, roughness: 0.6 });
  const handMat = new THREE.MeshStandardMaterial({ color: lightDial ? 0x1a1a1e : accentHex, metalness: 1, roughness: 0.25 });
  const secMat = new THREE.MeshStandardMaterial({ color: 0xd9b46b, metalness: 0.9, roughness: 0.3 });
  const glassMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, metalness: 0, roughness: 0.03, transparent: true, opacity: 0.16, clearcoat: 1, clearcoatRoughness: 0.02, envMapIntensity: 1.6, side: THREE.DoubleSide, depthWrite: false });
  const strapMat = strapHex === null ? brushed : new THREE.MeshStandardMaterial({ color: strapHex, metalness: 0, roughness: opts.strap === 'rubber' ? 0.7 : 0.9 });

  const watch = new THREE.Group();

  // Case (lathe profile)
  const profile = [[0, -0.18], [0.9, -0.18], [1.0, -0.12], [1.06, 0.0], [1.04, 0.13], [0.98, 0.19], [0.88, 0.19], [0.86, 0.06], [0, 0.06]]
    .map((p) => new THREE.Vector2(p[0], p[1]));
  const caseMesh = new THREE.Mesh(new THREE.LatheGeometry(profile, 128), polished);
  caseMesh.material.side = THREE.DoubleSide;
  watch.add(caseMesh);

  // Dial
  const dial = new THREE.Mesh(new THREE.CircleGeometry(0.86, 128), dialMat);
  dial.rotation.x = -Math.PI / 2; dial.position.y = 0.07;
  watch.add(dial);

  let hands;
  if (diver) {
    hands = buildDiver(watch, opts, { polished, brushed, glassMat });
  } else {
  // Bezel
  const bezel = new THREE.Mesh(new THREE.TorusGeometry(0.93, 0.075, 32, 128), polished);
  bezel.rotation.x = Math.PI / 2; bezel.position.y = 0.19;
  watch.add(bezel);
  // Fluted bezel notches
  const notchGeo = new THREE.BoxGeometry(0.03, 0.05, 0.07);
  for (let i = 0; i < 72; i++) {
    const n = new THREE.Mesh(notchGeo, brushed);
    const a = (i / 72) * Math.PI * 2;
    n.position.set(Math.sin(a) * 0.93, 0.26, Math.cos(a) * 0.93);
    n.rotation.y = -a; watch.add(n);
  }

  // Applied indices
  const idxGeo = new THREE.BoxGeometry(0.05, 0.03, 0.17);
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const r = 0.68;
    const make = (dx) => {
      const m = new THREE.Mesh(idxGeo, accent);
      const px = Math.sin(a) * r + Math.cos(a) * dx;
      const pz = -Math.cos(a) * r + Math.sin(a) * dx;
      m.position.set(px, 0.09, pz); m.rotation.y = -a; return m;
    };
    if (i === 0) { watch.add(make(-0.045)); watch.add(make(0.045)); }
    else watch.add(make(0));
  }

  // Hands
  hands = {};
  const mkHand = (w, len, y, mat, tail) => {
    const pivot = new THREE.Group(); pivot.position.y = y;
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, 0.018, len), mat);
    m.position.z = -len / 2 + 0.04; pivot.add(m);
    if (tail) { const t = new THREE.Mesh(new THREE.BoxGeometry(w * 2.2, 0.018, tail), mat); t.position.z = tail / 2; pivot.add(t); }
    watch.add(pivot); return pivot;
  };
  hands.hour = mkHand(0.075, 0.48, 0.11, handMat);
  hands.minute = mkHand(0.05, 0.72, 0.125, handMat);
  hands.second = mkHand(0.014, 0.78, 0.14, secMat, 0.18);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.03, 32), accent);
  cap.position.y = 0.15; watch.add(cap);

  // Crystal
  const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.88, 0.88, 0.05, 128), glassMat);
  glass.position.y = 0.21; glass.renderOrder = 10; watch.add(glass);
  }

  // Crown
  const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.16, 48), brushed);
  crown.rotation.z = Math.PI / 2; crown.position.set(1.14, 0.0, 0); watch.add(crown);
  const crownRing = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.02, 16, 48), polished);
  crownRing.rotation.y = Math.PI / 2; crownRing.position.set(1.22, 0, 0); watch.add(crownRing);
  const guard = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.22, 0.42), polished);
  guard.position.set(1.0, -0.02, 0); watch.add(guard);

  // Lugs
  const lugGeo = new THREE.BoxGeometry(0.17, 0.24, 0.36);
  [[-0.62, 1.0], [0.62, 1.0], [-0.62, -1.0], [0.62, -1.0]].forEach(([x, z]) => {
    const l = new THREE.Mesh(lugGeo, polished); l.position.set(x, -0.03, z); watch.add(l);
  });
  // Spring bars / end links
  [1.02, -1.02].forEach((z) => { const e = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.12, 0.2), brushed); e.position.set(0, -0.03, z); watch.add(e); });

  // Strap / bracelet along an arc around the wrist (axis = x)
  const R = 1.95; const cy = -R + 0.24;
  const theta0 = Math.asin(1.1 / R);
  const addStrap = (dir) => {
    if (strapHex === null) {
      const step = 0.135, count = 9;
      const centerGeo = new THREE.BoxGeometry(0.36, 0.1, 0.22);
      const outerGeo = new THREE.BoxGeometry(0.27, 0.1, 0.22);
      for (let i = 0; i < count; i++) {
        const th = theta0 + 0.12 + i * step;
        const link = new THREE.Group();
        link.position.set(0, cy + R * Math.cos(th), dir * R * Math.sin(th));
        link.rotation.x = dir * th;
        const c = new THREE.Mesh(centerGeo, polished); link.add(c);
        const l = new THREE.Mesh(outerGeo, brushed); l.position.x = -0.33; link.add(l);
        const r = new THREE.Mesh(outerGeo, brushed); r.position.x = 0.33; link.add(r);
        link.scale.x = 1 - i * 0.022;
        watch.add(link);
      }
    } else {
      const segs = 22, step = 0.058;
      const geo = new THREE.BoxGeometry(0.66, 0.06, 0.13);
      for (let i = 0; i < segs; i++) {
        const th = theta0 + 0.08 + i * step;
        const s = new THREE.Mesh(geo, strapMat);
        s.position.set(0, cy + R * Math.cos(th), dir * R * Math.sin(th));
        s.rotation.x = dir * th; s.scale.x = 1 - i * 0.012;
        watch.add(s);
      }
      // stitching hint
      const stitch = new THREE.MeshStandardMaterial({ color: 0xcdb58a, roughness: 0.8 });
      for (let i = 1; i < segs; i += 2) {
        const th = theta0 + 0.08 + i * step;
        [-0.28, 0.28].forEach((x) => {
          const st = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.012, 0.06), stitch);
          st.position.set(x * (1 - i * 0.012), cy + R * Math.cos(th) + 0.032, dir * R * Math.sin(th));
          st.rotation.x = dir * th; watch.add(st);
        });
      }
    }
  };
  addStrap(1); addStrap(-1);

  return { group: watch, hands };
}

export class SStudioWatch {
  constructor(el, opts = {}) {
    this.el = el;
    this.opts = Object.assign({ metal: 'steel', dial: 'navy', strap: 'bracelet', style: 'classic', bezel: 'black', autoRotate: true, interactive: true, tiltX: 0.78, scale: 1 }, opts);
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.spin = 0; this.spinVel = 0; this.dragging = false; this.visible = true; this.t = 0;
    this.mouse = { x: 0, y: 0 };
    this.init();
  }

  init() {
    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    r.toneMapping = THREE.ACESFilmicToneMapping; r.toneMappingExposure = 1.0;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.setClearColor(0x000000, 0);
    this.renderer = r; this.el.appendChild(r.domElement);

    this.scene = new THREE.Scene();
    const pmrem = new THREE.PMREMGenerator(r);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    this.camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
    this.camera.position.set(0, 1.1, 8.2 / (this.opts.scale || 1));
    this.camera.lookAt(0, -0.55, 0);

    const key = new THREE.DirectionalLight(0xfff1dc, 2.2); key.position.set(3, 6, 4); this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xd9b46b, 1.6); rim.position.set(-5, 2, -4); this.scene.add(rim);
    const fill = new THREE.DirectionalLight(0x9fb4ff, 0.6); fill.position.set(-3, -2, 5); this.scene.add(fill);

    this.tilt = new THREE.Group(); this.tilt.rotation.x = this.opts.tiltX;
    this.turn = new THREE.Group(); this.tilt.add(this.turn);
    const built = buildWatch(this.opts); this.hands = built.hands;
    this.turn.add(built.group);
    this.scene.add(this.tilt);

    this.resize();
    this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(this.el);
    this.io = new IntersectionObserver((e) => { this.visible = e[0].isIntersecting; if (this.visible) this.loop(); }, { threshold: 0 });
    this.io.observe(this.el);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && this.visible) this.loop(); });

    if (this.opts.interactive) this.bindPointer();
    this.el.classList.add('is-ready');
    this.loop();
  }

  bindPointer() {
    const el = this.el; let lastX = 0, lastY = 0;
    el.addEventListener('pointerdown', (e) => { this.dragging = true; lastX = e.clientX; lastY = e.clientY; el.setPointerCapture(e.pointerId); });
    el.addEventListener('pointermove', (e) => {
      if (this.dragging) {
        const dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY;
        this.spinVel = dx * 0.008; this.spin += this.spinVel;
        this.tilt.rotation.x = THREE.MathUtils.clamp(this.tilt.rotation.x + dy * 0.005, 0.2, 1.3);
      } else {
        const rect = el.getBoundingClientRect();
        this.mouse.x = (e.clientX - rect.left) / rect.width - 0.5;
        this.mouse.y = (e.clientY - rect.top) / rect.height - 0.5;
      }
    });
    const up = () => { this.dragging = false; };
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up); el.addEventListener('pointerleave', () => { this.dragging = false; this.mouse.x = 0; this.mouse.y = 0; });
  }

  resize() {
    const w = this.el.clientWidth || 300, h = this.el.clientHeight || 300;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    this.render();
  }

  updateHands() {
    const d = new Date();
    const ms = d.getMilliseconds();
    const s = d.getSeconds() + ms / 1000;
    const m = d.getMinutes() + s / 60;
    const h = (d.getHours() % 12) + m / 60;
    // Sweep seconds like an automatic movement (smooth), hour/minute continuous.
    this.hands.second.rotation.y = -(s / 60) * Math.PI * 2;
    this.hands.minute.rotation.y = -(m / 60) * Math.PI * 2;
    this.hands.hour.rotation.y = -(h / 12) * Math.PI * 2;
  }

  render() {
    this.updateHands();
    this.renderer.render(this.scene, this.camera);
  }

  loop() {
    if (this.raf) return;
    const tick = () => {
      this.raf = null;
      if (!this.visible || document.hidden) return;
      this.t += 0.016;
      if (!this.dragging) {
        if (this.opts.autoRotate && !this.reduce) this.spin += 0.0035;
        this.spin += this.spinVel; this.spinVel *= 0.94;
        // gentle float + parallax toward the cursor
        const targetX = this.opts.tiltX + this.mouse.y * 0.25;
        this.tilt.rotation.x += (targetX - this.tilt.rotation.x) * 0.05;
        this.tilt.rotation.z += (this.mouse.x * 0.12 - this.tilt.rotation.z) * 0.05;
      }
      this.turn.rotation.y = this.spin;
      this.tilt.position.y = Math.sin(this.t * 0.8) * 0.06 - 0.1;
      this.render();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  destroy() {
    this.visible = false; if (this.raf) cancelAnimationFrame(this.raf);
    this.ro.disconnect(); this.io.disconnect(); this.renderer.dispose(); this.renderer.domElement.remove();
  }
}

export function mountAll(root = document) {
  root.querySelectorAll('[data-watch-3d]').forEach((el) => {
    if (el.__watch) return;
    el.__watch = new SStudioWatch(el, {
      metal: el.dataset.metal || 'steel',
      dial: el.dataset.dial || 'navy',
      strap: el.dataset.strap || 'bracelet',
      style: el.dataset.style || 'classic',
      bezel: el.dataset.bezel || 'black',
      brand: el.dataset.brand || '',
      line1: el.dataset.line1 || '',
      model: el.dataset.model || '',
      line2: el.dataset.line2 || '',
      autoRotate: el.dataset.autoRotate !== 'false',
      interactive: el.dataset.interactive !== 'false',
      tiltX: parseFloat(el.dataset.tiltX || '0.78'),
      scale: parseFloat(el.dataset.scale || '1')
    });
  });
}

window.SStudioWatch = { mount: (el, o) => new SStudioWatch(el, o), mountAll };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll());
else mountAll();
document.addEventListener('sstudio:mount-3d', () => mountAll());
document.addEventListener('shopify:section:load', (e) => mountAll(e.target));
